<template lang="html">
  <div class="">
    <v-container grid-list-md>
      <v-layout wrap>
        <v-flex
          xs12
          lg10
          xl8
          mx-auto
        >
          <v-layout wrap>
            <v-flex xs12>
              <v-card>
                <v-card-title
                  id="initialSections"
                  class="headline"
                >
                  Initial Sections
                </v-card-title>
                <v-card-text class="grey lighten-3 py-4">
                  <FormsSectionBuilder
                    :fields="fields"
                    :sections="initialSection"
                    :sections-all="Object.assign({}, initialSection, allFormSections)"
                    :save-path="`calculators/${calculatorId}/structure/mapping/initialSection`"
                  />
                </v-card-text>
              </v-card>
            </v-flex>

            <v-flex xs12>
              <v-card>
                <v-card-title
                  id="forms"
                  class="headline"
                >
                  <v-layout align-center>
                    <v-flex>Forms</v-flex>
                    <v-btn
                      icon
                      color="primary"
                      @click="newForm"
                    >
                      <v-icon>add</v-icon>
                    </v-btn>
                  </v-layout>
                </v-card-title>

                <v-divider />

                <v-layout
                  ma-0
                  :wrap="$vuetify.breakpoint.smAndDown"
                >
                  <v-flex
                    pa-0
                    :shrink="!$vuetify.breakpoint.smAndDown"
                  >
                    <v-list>
                      <v-list-item-group
                        v-model="activeForm"
                        color="primary"
                      >
                        <v-list-item
                          v-for="form in sortedForms"
                          :key="form.id"
                        >
                          <v-list-item-content>
                            <v-list-item-title
                              :class="{'font-italic': !form.name}"
                              style="min-width: 150px;"
                              v-text="form.name || '[add name]'"
                            />
                          </v-list-item-content>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-flex>

                  <v-divider vertical />

                  <v-flex
                    xs12
                    pa-0
                  >
                    <v-tabs-items
                      v-model="activeForm"
                      class="transparent"
                    >
                      <v-tab-item
                        v-for="(form, i) in sortedForms"
                        :key="form.id"
                        class="fill-height"
                      >
                        <v-flex
                          v-if="activeForm === i"
                          xs12
                          pa-4
                        >
                          <v-layout align-center>
                            <SiConditionEditor
                              v-model="form.condition"
                              :title="`<strong>${form.name}</strong> condition`"
                              :fields="fields"
                              @input="save(form.id)"
                            >
                              <template
                                v-if="!form.condition"
                                #activator="{ on }"
                              >
                                <v-btn
                                  text
                                  color="error"
                                  v-on="on"
                                >
                                  <v-icon left>
                                    arrow_upward
                                  </v-icon>Set form condition
                                </v-btn>
                              </template>
                            </SiConditionEditor>
                            <v-flex v-if="form.condition">
                              <SiConditionChips :condition="form.condition" />
                            </v-flex>
                            <v-btn 
                              size="small" 
                              icon
                              :disabled="i === 0"
                              @click="moveForm(form.id, i, i - 1)"
                            >
                              <v-icon>expand_less</v-icon>
                            </v-btn>
                            <v-btn 
                              size="small" 
                              icon
                              :disabled="i === sortedForms.length - 1"
                              @click="moveForm(form.id, i, i + 1)"
                            >
                              <v-icon>expand_more</v-icon>
                            </v-btn>
                          </v-layout>
                        </v-flex>
                        <v-expand-transition>
                          <div v-if="!hideFormDetails[form.id]">
                            <v-flex
                              xs12
                              pa-4
                            >
                              <SiInputText 
                                :value="form.name" 
                                outlined 
                                label="Form name" 
                                hint="System only, not seen in calculator" 
                                @change="v => { form.name = v; save(form.id, true) }"
                              />
                              <SiInputTextarea 
                                v-model="form.description"
                                outlined 
                                rows="2" 
                                label="Form description" 
                                hint="System only, not seen in calculator" 
                                @input="save(form.id)"
                              />
                            </v-flex>
                          </div>
                        </v-expand-transition>
                        <v-flex
                          xs12
                          text-center
                        >
                          <v-btn 
                            text
                            color="grey"
                            small
                            @click="hideDetails(form.id)"
                          >
                            <v-icon left>
                              {{ hideFormDetails[form.id] ? 'expand_more' : 'expand_less' }}
                            </v-icon> 
                            {{ hideFormDetails[form.id] ? 'details' : 'hide' }}
                          </v-btn>
                        </v-flex>

                        <v-card-text class="grey lighten-3">
                          <FormsSectionBuilder
                            :fields="fields"
                            :sections="form.sections"
                            :sections-all="Object.assign({}, initialSection, form.sections)"
                            :save-path="`calculators/${calculatorId}/structure/mapping/forms/${form.id}/sections`"
                          />
                        </v-card-text>
                        <v-card-actions>
                          <v-spacer />
                          <SiDeleteButton
                            title="Delete Form?"
                            :temessagext="`Are you certain you want to delete <strong>${form.name}</strong>?`"
                            :challenge="form.name"
                            @delete="deleteForm(form.id)"
                          >
                            <template #activator="{ on }">
                              <v-btn
                                text
                                color="grey"
                                v-on="on"
                              >
                                Delete Form
                              </v-btn>
                            </template>
                          </SiDeleteButton>
                          <v-spacer />
                        </v-card-actions>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { forEach } from 'lodash'
import helpers from '@/mixins/helpers'
import firebase from '@/mixins/firebase'
import { rtdb } from '@/plugins/firebase'
import drag from '@/mixins/drag'
import conditions from '@/mixins/conditions'
import FormsSectionBuilder from './components/FormsSectionBuilder.vue'

export default {
  components: {
    FormsSectionBuilder
  },
  mixins: [ helpers, drag, firebase, conditions ],
  props: {
    fields: Object,
    mapping: Object,
    calculatorId: String
  },
  data () {
    return {
      activeForm: 0,
      newFormDialog: false,
      newFormName: '',
      savePending: false,
      saveTimeout: null,
      saveActive: false,
      // initialSection: {},
      hideFormDetails: {},
      initialFields: [
        { name: 'super_type', condition: {}, index: 0 },
        { name: 'interest_type', condition: {}, index: 1 },
        { name: 'input_select', condition: {}, index: 2 }
      ],
      // forms: {},
      mounted: false
    }
  },
  computed: {
    forms () {
      return this.mapping.forms
    },
    sortedForms () {
      return this._sortObject(this.forms || {})
    },
    initialSection () {
      return this.mapping.initialSection
    },
    allFormSections () {
      let sections = {}
      forEach(this.forms, form => {
        sections = Object.assign(sections, form.sections)
      })
      return sections
    },
    formsPath () {
      return `calculators/${this.calculatorId}/structure/mapping/forms`
    }
  },
  watch: {
    '$route.params.sub' () {
      this.activeForm = undefined
      this.$nextTick(() => this.activeForm = 0)
    },
    '$route.params.id' (v) {
      let { formId, sectionId } = this.$route.query
      let formIndex = Math.max(this.sortedForms.findIndex(f => f.id === formId), 0)

      this.$vuetify.goTo(0, { duration: 0 })

      this.activeForm = undefined
      this.$nextTick(() => {
        if ( v === 'forms' && formId ) {
          this.activeForm = formIndex
          if (sectionId) {
            setTimeout(() => this.$vuetify.goTo(`#${sectionId}`), 300);
          } else {
            setTimeout(() => this.$vuetify.goTo(formId === 'initialSections' ? '#initialSections' : '#forms'), 10)
          }
        } else {
          this.activeForm = 0
          this.$vuetify.goTo(0)
        }
      })
    }

  },
  methods: {
    hideDetails (formId) {
      this.$set(this.hideFormDetails, formId, !this.hideFormDetails[formId])
    },
    newForm () {
      let index = this.sortedForms.length
      rtdb.ref(this.formsPath).push({ new: true, index })
      this.activeForm = index
    },
    save (formId, instant) {
      var form = this.forms[formId]
      var timer = instant ? 100 : 800
      var savePath = `${this.formsPath}/${formId}`

      delete form.new

      this.savePending = true

      if (this.saveTimeout) { clearTimeout(this.saveTimeout) }

      if (this.saveFunction) { this.saveFunction = null }

      this.saveFunction = () => {
        this._dbSave('rtdb', savePath, form)
          .then(() => {
            this.savePending = false
            setTimeout(() => {
              this.saveActive = false
            }, 500)
          })
      }

      this.saveTimeout = setTimeout(() => {
        this.saveActive = true
        this.saveFunction()
        this.saveFunction = null
        this.saveTimeout = null
      }, timer)
    },
    deleteForm (formId) {
      rtdb.ref(`${this.formsPath}/${formId}`).remove()
    },
    moveForm (id, index, newIndex) {
      let forms = [...this.sortedForms]
      let updates = {}
      
      this._arrayMove (forms, index, newIndex)

      forms.forEach(({ id }, i) => updates[`${this.formsPath}/${id}/index`] = i)

      rtdb.ref().update(updates)
    },
  }
}
</script>

<style lang="css" scoped>
</style>
